import React, { useEffect, useMemo, useState } from 'react';
import FitnessExerciseEditor, { createEmptyExercise, normalizeExercise } from './FitnessExerciseEditor';

const WEEKDAYS = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const GOALS = ['General fitness', 'Strength', 'Mobility', 'Cardio', 'Yoga', 'Sport'];
const STATUSES = ['Active', 'Paused', 'Completed'];

/**
 * Creates day.
 *
 * @param {number} index - index numeric value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
const makeDay = (index) => ({
  id: `day_${Date.now()}_${index}`,
  weekday: '',
  savedWorkoutId: '',
  title: `Day ${index}`,
  strengthExercises: [createEmptyExercise()],
  notes: '',
});

/**
 * Performs the initial routine operation.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function initialRoutine(value) {
  const count = Math.min(Math.max(Number(value?.daysPerWeek || value?.days?.length || 3), 1), 7);
  const days = Array.isArray(value?.days) && value.days.length
    ? value.days.map((day, index) => ({ ...makeDay(index + 1), ...day, strengthExercises: (day.strengthExercises || []).map(normalizeExercise) }))
    : Array.from({ length: count }, (_, index) => makeDay(index + 1));
  return {
    id: value?.id || null,
    title: value?.title || '', goal: value?.goal || 'General fitness',
    daysPerWeek: count, startDate: value?.startDate || '', endDate: value?.endDate || '',
    status: value?.status || 'Active', days, notes: value?.notes || '', favorite: Boolean(value?.favorite),
  };
}

/**
 * Normalizes routine input.
 *
 * @param {*} routine - routine value.
 * @returns {*} The derived operation result.
 */
export function cleanRoutineInput(routine) {
  return {
    title: routine.title.trim(), goal: routine.goal,
    daysPerWeek: Number(routine.daysPerWeek), startDate: routine.startDate, endDate: routine.endDate,
    status: routine.status,
    days: routine.days.map((day) => ({
      id: day.id, weekday: day.weekday, savedWorkoutId: day.savedWorkoutId || '', title: day.title.trim(),
      strengthExercises: day.strengthExercises.filter((exercise) => exercise.name.trim()).map((exercise) => ({
        id: exercise.id, name: exercise.name.trim(), muscleGroup: exercise.muscleGroup || '', equipment: exercise.equipment || '',
        category: exercise.category || '', trackingMode: exercise.trackingMode || 'strength', weightUnit: exercise.weightUnit || 'lb',
        isCustom: Boolean(exercise.isCustom), notes: '', sets: exercise.sets.map((set) => ({
          id: set.id, reps: Number(set.reps || 0), weight: String(set.weight || ''), durationSeconds: Number(set.durationSeconds || 0),
          distance: String(set.distance || ''), restSeconds: Number(set.restSeconds || 0), completed: Boolean(set.completed),
        })),
      })),
      notes: day.notes.trim(),
    })),
    notes: routine.notes.trim(), favorite: Boolean(routine.favorite),
  };
}

/** Creates or edits a weekly routine and its reusable workout-day exercise definitions. */
export default function FitnessRoutineForm({ initialData, catalog, isSaving, onSave, onCancel, onDirtyChange }) {
  const seed = useMemo(() => initialRoutine(initialData), [initialData]);
  const [routine, setRoutine] = useState(seed);
  const [step, setStep] = useState('routine');
  const [editingDay, setEditingDay] = useState(null);
  const [error, setError] = useState('');
  const steps = [['routine', 'Routine'], ['days', 'Workout days'], ['notes', 'Notes']];

  useEffect(() => { setRoutine(seed); setStep('routine'); setEditingDay(null); }, [seed]);
  useEffect(() => { onDirtyChange?.(JSON.stringify(routine) !== JSON.stringify(seed)); }, [onDirtyChange, routine, seed]);

  /**
   * Updates the requested operation.
   *
   * @param {*} patch - patch value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const update = (patch) => setRoutine((current) => ({ ...current, ...patch }));
  /**
   * Updates day.
   *
   * @param {string} id - Stable identifier of the target resource.
   * @param {*} patch - patch value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const updateDay = (id, patch) => update({ days: routine.days.map((day) => day.id === id ? { ...day, ...patch } : day) });
  /**
   * Performs the change frequency operation.
   *
   * @param {*} next - next value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const changeFrequency = (next) => {
    const count = Math.min(Math.max(Number(next), 1), 7);
    if (count < routine.days.length && !window.confirm('Remove workout days? The details saved in those days will be removed.')) return;
    const days = count > routine.days.length
      ? [...routine.days, ...Array.from({ length: count - routine.days.length }, (_, index) => makeDay(routine.days.length + index + 1))]
      : routine.days.slice(0, count);
    update({ daysPerWeek: count, days });
  };
  /**
   * Performs the requested operation.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const submit = async () => {
    setError('');
    if (!routine.title.trim()) { setError('Enter a routine name.'); setStep('routine'); return; }
    const titles = routine.days.map((day) => day.title.trim().toLowerCase()).filter(Boolean);
    const weekdays = routine.days.map((day) => day.weekday).filter(Boolean);
    if (new Set(titles).size !== titles.length) { setError('Workout day titles must be unique.'); setStep('days'); return; }
    if (new Set(weekdays).size !== weekdays.length) { setError('Choose a different weekday for each workout day.'); setStep('days'); return; }
    if (routine.days.some((day) => day.strengthExercises.some((exercise) => exercise.isCustom && !exercise.name.trim()))) { setError('Choose a name for each custom exercise.'); setStep('days'); return; }
    await onSave({ ...routine, input: cleanRoutineInput(routine) });
  };
  const currentIndex = steps.findIndex(([id]) => id === step);

  if (editingDay) {
    const day = routine.days.find((item) => item.id === editingDay);
    return (
      <div className="recipe-form fitness-form">
        <section className="recipe-form-section priority-section">
          <div className="section-heading inline-heading"><div><p className="eyebrow">Workout day</p><h3>{day.title}</h3></div><button type="button" className="btn-secondary" onClick={() => setEditingDay(null)}>Done</button></div>
          <div className="form-grid two-columns">
            <div className="form-group"><label>Workout title</label><input value={day.title} onChange={(event) => updateDay(day.id, { title: event.target.value })} /></div>
            <div className="form-group"><label>Weekday</label><select value={day.weekday} onChange={(event) => updateDay(day.id, { weekday: event.target.value })}>{WEEKDAYS.map((value) => <option value={value} key={value}>{value || 'Not set'}</option>)}</select></div>
          </div>
        </section>
        <section className="recipe-form-section"><h3>Exercises</h3><FitnessExerciseEditor catalog={catalog} exercises={day.strengthExercises} onChange={(strengthExercises) => updateDay(day.id, { strengthExercises })} /></section>
      </div>
    );
  }

  return (
    <div className="recipe-form fitness-form">
      <div className="hobby-flow-stepper" role="tablist" aria-label="Routine editor steps">
        {steps.map(([id, label], index) => <button type="button" key={id} className={step === id ? 'active' : index < currentIndex ? 'complete' : ''} onClick={() => setStep(id)}><span>{index + 1}</span>{label}</button>)}
      </div>
      {error && <div className="inline-error-banner" role="alert">{error}</div>}
      {step === 'routine' && <section className="recipe-form-section priority-section"><h3>Routine</h3><div className="form-grid two-columns">
        <div className="form-group form-grid-full"><label>Routine name <span className="required-mark">Required</span></label><input value={routine.title} onChange={(event) => update({ title: event.target.value })} placeholder="My 3-day split" /></div>
        <div className="form-group"><label>Goal</label><select value={routine.goal} onChange={(event) => update({ goal: event.target.value })}>{GOALS.map((value) => <option key={value}>{value}</option>)}</select></div>
        <div className="form-group"><label>Status</label><select value={routine.status} onChange={(event) => update({ status: event.target.value })}>{STATUSES.map((value) => <option key={value}>{value}</option>)}</select></div>
        <div className="form-group"><label>Days per week</label><div className="fitness-number-stepper"><strong>{routine.daysPerWeek} days</strong><button type="button" onClick={() => changeFrequency(routine.daysPerWeek - 1)}>−</button><button type="button" onClick={() => changeFrequency(routine.daysPerWeek + 1)}>＋</button></div></div>
        <div className="form-group"><label>Start date</label><input type="date" value={routine.startDate} onChange={(event) => update({ startDate: event.target.value })} /></div>
        <div className="form-group"><label>End date</label><input type="date" min={routine.startDate || undefined} value={routine.endDate} onChange={(event) => update({ endDate: event.target.value })} /></div>
        <div className="form-group form-grid-full"><button type="button" className={`favorite-toggle inline-favorite-toggle ${routine.favorite ? 'active' : ''}`} onClick={() => update({ favorite: !routine.favorite })}>{routine.favorite ? 'Favorite routine' : 'Mark as favorite'}</button></div>
      </div></section>}
      {step === 'days' && <section className="recipe-form-section"><h3>Workout days</h3><div className="fitness-routine-days">{routine.days.map((day) => <button type="button" className="fitness-routine-day-row" key={day.id} onClick={() => setEditingDay(day.id)}><span className="fitness-option-icon">◆</span><span><strong>{day.title}</strong><small>{[day.weekday, `${day.strengthExercises.filter((item) => item.name).length} exercises`].filter(Boolean).join(' • ')}</small></span><b>›</b></button>)}</div></section>}
      {step === 'notes' && <section className="recipe-form-section"><h3>Notes</h3><div className="form-group"><label>Notes</label><textarea rows="5" value={routine.notes} onChange={(event) => update({ notes: event.target.value })} /></div></section>}
      <div className="form-actions sticky-actions"><button type="button" className="btn-ghost" onClick={() => onCancel({ confirmDiscard: true })}>Cancel</button><div className="stepper-actions">{currentIndex > 0 && <button type="button" className="btn-secondary" onClick={() => setStep(steps[currentIndex - 1][0])}>Back</button>}{currentIndex < steps.length - 1 ? <button type="button" className="btn-primary" onClick={() => setStep(steps[currentIndex + 1][0])}>Next</button> : <button type="button" className="btn-primary" disabled={isSaving} onClick={submit}>{isSaving ? 'Saving' : 'Save routine'}</button>}</div></div>
    </div>
  );
}
